import * as Sentry from '@sentry/react'
import { makeAutoObservable } from 'mobx'
import Loader from 'reusable/mobx-helpers/Loader'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { apiClient } from 'api/trpc/trpc-client'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'
import { notNull } from '@gap/utils/utils'
import { XeroConsentDto } from '@gap/backend/app-services/xero/models/XeroConsentDto'
import variables from 'variables/variables'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'
import XpmConnectionStore from './XpmConnectionStore'
import { ClientSourceActions } from 'features/org/edit/integrations/AdvisorIntegrationCard'
import { confirmClientSourceSwitch } from 'features/clients/confirmClientSourceSwitch'

export type XeroOriginType = 'app' | 'trial'

const XPM_RETURN_PATH_KEY = 'xpm-return-path'

interface XeroXpmConectStoreProps {
   originType: XeroOriginType
   orgGuardStore: OrgGuardStore
   xpmConnectionStore: XpmConnectionStore
   /** Where the card shows the org's client source, if anywhere: the create client modal's main row or the integrations page's capability rows. */
   clientSourceSlot?: 'main-row' | 'rows'
}

/** Per-surface card behaviour for the XPM integration; connection state lives in XpmConnectionStore. */
export default class XeroXpmConectStore {
   loader = new Loader(false)
   consent: XeroConsentDto | undefined = undefined
   redirecting = false
   sp

   constructor(sp: XeroXpmConectStoreProps) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get connection() {
      return this.sp.xpmConnectionStore
   }

   get xeroCallbackUrl() {
      // Only `/xero-callback/app` and `/xero-callback/trial` are registered as redirect URIs
      // with Xero.
      const path = this.sp.originType === 'trial' ? 'trial' : 'app'
      return `${variables.regionBaseUrl}/xero-callback/${path}`
   }

   get xeroAuthorised() {
      return this.connection.xeroAuthorised
   }

   get xpmName() {
      return this.connection.xpmName
   }

   get isSyncing() {
      return this.connection.isSyncing
   }

   get integrationCardStatus() {
      return this.connection.cardStatus
   }

   /** True whenever the card must not offer an action — fetching, exchanging, or leaving for Xero. */
   get busy() {
      return this.loader.isLoading || this.connection.busy || this.redirecting
   }

   /** True while XPM is the org's client source. */
   get isClientSource() {
      return this.sp.orgGuardStore.userOrg.syncVia === 'xpm'
   }

   get isConnected() {
      return this.integrationCardStatus === 'connected'
   }

   /** True while FYI is the org's client source. */
   get fyiIsClientSource() {
      return this.sp.orgGuardStore.userOrg.syncVia === 'fyi'
   }

   /** Whether the integrations page shows the Clients row, connected or not. */
   get showClientSourceRow() {
      return this.sp.clientSourceSlot === 'rows' && this.sp.orgGuardStore.hasFyiClientSyncAccess
   }

   /** While FYI holds the source and XPM is not connected, the row gives way to a note. */
   get showFyiHoldsSourceNote() {
      return this.showClientSourceRow && !this.isConnected && this.fyiIsClientSource
   }

   /** The Clients row's text and the action the card can offer in the current state. */
   get clientsRow(): { description: string; action: 'sync' | 'set-source' | 'none' } {
      if (!this.isConnected) return { description: 'Use XPM as your client source', action: 'none' }
      if (this.isClientSource) return { description: 'Client source', action: 'sync' }
      return { description: 'Use XPM as your client source', action: 'set-source' }
   }

   /** The card's client source state and actions, on the surfaces that show it. */
   get clientSource(): ClientSourceActions | undefined {
      if (!this.sp.clientSourceSlot) return undefined
      return {
         isCurrent: this.isClientSource,
         onSyncNow: this.syncNow,
         syncing: this.isSyncing,
         onSetAsSource: this.setAsClientSource,
         settingSource: this.connection.settingSource,
      }
   }

   /** Fetches the consent URL when the org isn't connected, unless an exchange is already running. */
   initialiseSettings = async () => {
      if (this.connection.busy) return
      await this.loader.executeLoading(async () => {
         try {
            if (this.connection.xeroAuthorised) return
            this.consent = await apiClient.xpm.getConsentUrl.query({ xeroCallbackUrl: this.xeroCallbackUrl })
         } catch (error) {
            console.log(error)
         }
      })
   }

   connect = async () => {
      this.redirecting = true
      // The consent fetch on mount can fail silently, leaving nothing to redirect to.
      if (!this.consent) {
         await this.initialiseSettings()
      }
      sessionStorage.setItem(XPM_RETURN_PATH_KEY, window.location.pathname + window.location.search)
      window.location.href = notNull(this.consent).url
   }

   syncNow = async () => {
      await this.connection.runSync()
   }

   /** Makes XPM the client source, after confirming the switch when another integration holds it. */
   setAsClientSource = async () => {
      const previous = this.sp.orgGuardStore.userOrg.syncVia
      if (previous && !(await confirmClientSourceSwitch({ from: previous, to: 'xpm' }))) return
      await this.connection.becomeClientSource()
   }

   disconnect = async () => {
      try {
         this.connection.setDisconnecting()
         await apiClient.xpm.disconnect.mutate({})
         this.connection.setDisconnected()
         this.consent = await apiClient.xpm.getConsentUrl.query({ xeroCallbackUrl: this.xeroCallbackUrl })
      } catch (error: unknown) {
         this.connection.setDisconnectFailed()
         Sentry.captureException(error, {
            tags: { component: 'XeroXpmConnectStore', action: 'disconnect' },
         })
         snackbarError('We had trouble disconnecting from XPM. Please try again.')
      }
   }
}

export const { Provider: XeroXpmConectStoreProvider, useProvidedStore: useXeroXpmConectStore } = createStoreContext(XeroXpmConectStore)
